import { useEffect, useRef, useState } from "react";
import type { BoardViewportState } from "../../lib/board/boardViewport";
import { worldToClient } from "../../lib/board/boardViewport";
import { WbBoardScene } from "../../lib/board/wb-board-scene";
import { isFrameObject } from "../../editor/frame/frameContext";
import {
  isOverlayEditableObject,
  isStickyObject,
  isTextObject,
  measureTextObjectSize,
  measureTextObjectSizeAtWidth,
  mergeTextMeta,
  patchTextStyleAndFit,
  TEXT_BOX_MAX_W,
  TEXT_BOX_MIN_H,
  TEXT_BOX_MIN_W,
  textContent,
  textDisplayStyle,
  textWidthMode,
  grownTextWidth,
  textWidthGrows,
  isTextLikeObject,
} from "../../editor/textObject";
import { setTextSettings } from "../../editor/textSession";
import {
  readEditorModel,
  writeEditorModel,
  type EditorModel,
} from "../../editor/textEditorDom";
import {
  autoformatLine,
  backspaceAtMarker,
  enterInList,
  indentListLine,
  toggleListLines,
  type TextEdit,
} from "../../editor/textLists";
import {
  linkRangeAt,
  runsAutolink,
  runsReplace,
  runsSetHighlight,
  runsSetHref,
  runsTrimEdges,
  runsWithoutFontSize,
} from "../../editor/textRuns";
import {
  htmlToRuns,
  objectRuns,
  runsPatch,
  runsPlainText,
  runsToHtml,
} from "../../editor/textRuns";
import {
  registerTextSelectionFormatter,
  registerTextSelectionLinkInfo,
  TEXT_LINK_REQUEST_EVENT,
  type TextMarkCommand,
} from "../../editor/textFormatBridge";
import {
  clampRectToVisualViewport,
  useVisualViewport,
} from "../../hooks/useVisualViewport";

/**
 * Последнее нажатие по странице. Поле набора открывается двойным щелчком, и
 * курсор должен встать туда, куда щёлкнули, — как в Miro, а не в конец.
 * Слушаем на перехвате: доска гасит pointerdown, и иначе его не увидеть.
 */
let lastPointerDown: { x: number; y: number; at: number } | null = null;
if (typeof window !== "undefined") {
  window.addEventListener(
    "pointerdown",
    (ev) => {
      lastPointerDown = { x: ev.clientX, y: ev.clientY, at: Date.now() };
    },
    true
  );
}

/** Нажатие было только что — значит поле открыли щелчком, а не клавишей. */
const CLICK_TO_EDIT_MS = 700;

/** Место в тексте под точкой экрана — в разных браузерах свой способ. */
function caretRangeAt(x: number, y: number): Range | null {
  const doc = document as Document & {
    caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
  };
  if (typeof doc.caretPositionFromPoint === "function") {
    const pos = doc.caretPositionFromPoint(x, y);
    if (!pos) return null;
    const r = document.createRange();
    r.setStart(pos.offsetNode, pos.offset);
    r.collapse(true);
    return r;
  }
  if (typeof document.caretRangeFromPoint === "function") {
    return document.caretRangeFromPoint(x, y);
  }
  return null;
}

type Props = {
  objectId: string;
  scene: InstanceType<typeof WbBoardScene>;
  viewportRef: React.RefObject<HTMLDivElement | null>;
  viewport: BoardViewportState;
  getLiveViewport: () => BoardViewportState;
  layoutTick: number;
  onClose: () => void;
  /** Сдвинуть доску — чтобы курсор набора не уходил за край экрана. */
  onViewportChange?: (next: BoardViewportState) => void;
};

function useCameraViewport(
  viewport: BoardViewportState,
  getLiveViewport: () => BoardViewportState,
  layoutTick: number
) {
  const [live, setLive] = useState(viewport);

  useEffect(() => {
    setLive(getLiveViewport());
  }, [viewport, layoutTick, getLiveViewport]);

  useEffect(() => {
    const onLive = () => setLive(getLiveViewport());
    window.addEventListener("wb:live-viewport", onLive);
    return () => window.removeEventListener("wb:live-viewport", onLive);
  }, [getLiveViewport]);

  return live;
}

export function TextOverlayEditor({
  objectId,
  scene,
  viewportRef,
  viewport,
  getLiveViewport,
  layoutTick,
  onClose,
  onViewportChange,
}: Props) {
  const editorRef = useRef<HTMLDivElement>(null);
  /**
   * Последнее выделение в поле. Поле размера на панели забирает фокус —
   * без фокуса и выделение в тексте пропадает. Команда с панели возвращает
   * его отсюда, и размер ложится на выделенный кусок, а не на всю надпись.
   */
  const savedRangeRef = useRef<Range | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const closingRef = useRef(false);
  const liveTimerRef = useRef(0);
  const lastLiveSentRef = useRef("");
  const editStartRef = useRef<{
    text: string;
    w: number;
    h: number;
    runs: ReturnType<typeof objectRuns>;
  } | null>(null);
  const cam = useCameraViewport(viewport, getLiveViewport, layoutTick);
  const vv = useVisualViewport();
  const obj = scene.findObject(objectId);
  const isFrame = Boolean(obj && isFrameObject(obj));

  /**
   * Поле набора стоит экранными координатами, а камеру доска пишет в DOM сама,
   * каждым кадром. Перерисовка через React отставала — на инерции после
   * прокрутки текст заметно улетал за доской и возвращался на место только
   * при остановке. Поэтому на время движения место полю пишем тем же кадром.
   */
  useEffect(() => {
    const onLive = () => {
      const el = isFrame ? inputRef.current : editorRef.current;
      const vpEl = viewportRef.current;
      const o = scene.findObject(objectId);
      if (!el || !vpEl || !o) return;
      // Экранная клавиатура поджимает экран, и поле притянуто к видимой
      // области — туда его ставит обычная отрисовка, мешать ей не нужно.
      const vvh = typeof window !== "undefined" && window.visualViewport
        ? window.visualViewport.height
        : 0;
      const layoutH = typeof window !== "undefined" ? window.innerHeight : 0;
      if (vvh > 0 && layoutH - vvh > 120) return;
      const rect = vpEl.getBoundingClientRect();
      const at = worldToClient(
        Number(o.x) || 0,
        (Number(o.y) || 0) + (isFrame ? -30 : 0),
        rect,
        getLiveViewport()
      );
      el.style.left = `${at.x}px`;
      el.style.top = `${at.y}px`;
    };
    window.addEventListener("wb:live-viewport", onLive);
    return () => window.removeEventListener("wb:live-viewport", onLive);
  }, [getLiveViewport, isFrame, objectId, scene, viewportRef]);

  function readDraftRaw(): string {
    if (isFrame) return inputRef.current?.value ?? "";
    const el = editorRef.current;
    if (!el) return "";
    // Текст берём из разобранных кусков: там же учтены <br> и переносы строк,
    // которые браузер вставляет при наборе.
    return runsPlainText(htmlToRuns(el));
  }

  /** Набранное с оформлением: что жирное, что цветное. */
  function readDraftRuns(): ReturnType<typeof htmlToRuns> {
    const el = editorRef.current;
    if (!el || isFrame) return [];
    return htmlToRuns(el);
  }

  /** Патч для live-sync наблюдателям (без trim — видно набор по мере ввода). */
  function buildLivePatch(raw: string): Record<string, unknown> | null {
    const current = scene.findObject(objectId);
    if (!current) return null;

    if (isFrameObject(current)) {
      if (raw === textContent(current)) return null;
      return { title: raw.slice(0, 256) };
    }

    // Оформление кусками (часть текста жирная/цветная). Сравниваем отдельно:
    // при форматировании выделенного куска текст тот же, и без этой проверки
    // правка не уходила бы ни на сервер, ни другому участнику.
    const nextRuns = runsPatch(readDraftRuns()).runs ?? null;
    const runsChanged =
      JSON.stringify(objectRuns(current) ?? null) !== JSON.stringify(nextRuns);

    const fixedWidth = isTextObject(current) && textWidthMode(current) === "fixed";
    if (fixedWidth && !raw.trim()) {
      if (raw === textContent(current) && !runsChanged) return null;
      return { text: raw, runs: nextRuns };
    }
    // Меряем по разметке кусков, если оформление разное внутри надписи.
    const measureHtml = nextRuns ? runsToHtml(nextRuns) : undefined;
    const fitted = measureDraft(current, raw, measureHtml);

    const patch: Record<string, unknown> = { text: raw };
    if (!isFrame) patch.runs = nextRuns;
    if (fitted) {
      patch.w = fitted.w;
      patch.h = fitted.h;
    }

    const prev = textContent(current);
    const sameText = raw === prev;
    const sameSize =
      !fitted ||
      (Math.round(Number(current.w) || 0) === fitted.w &&
        Math.round(Number(current.h) || 0) === fitted.h);
    if (sameText && sameSize && !runsChanged) return null;
    return patch;
  }

  function flushLiveSync() {
    if (closingRef.current || !scene.canEdit) return;
    const raw = readDraftRaw();
    // В отпечаток входит и оформление: иначе «сделать кусок жирным» при том же
    // тексте считалось бы «ничего не изменилось» и не уходило бы другим.
    const sig = `${objectId}:${raw}:${isFrame ? "" : JSON.stringify(readDraftRuns())}`;
    if (sig === lastLiveSentRef.current) return;
    const patch = buildLivePatch(raw);
    if (!patch) {
      lastLiveSentRef.current = sig;
      return;
    }
    lastLiveSentRef.current = sig;
    // Без undo: одно undo на финальный commit от состояния на старте редактирования.
    void scene.commitBatchPatches(
      [{ id: objectId, patch }],
      `text-live-${objectId}-${Date.now()}`,
      null
    );
  }

  /**
   * Оформить выделенный кусок текста (часть надписи), а не всю надпись.
   * Возвращает false, если выделения внутри поля нет — тогда панель
   * меняет оформление всего объекта, как раньше.
   */
  /** Вернуть выделение в поле, если фокус сейчас на панели. */
  function restoreEditorSelection() {
    const el = editorRef.current;
    const sel = window.getSelection();
    if (!el || !sel) return;
    if (sel.rangeCount > 0 && el.contains(sel.getRangeAt(0).commonAncestorContainer)) return;
    const saved = savedRangeRef.current;
    if (!saved || !el.contains(saved.commonAncestorContainer)) return;
    el.focus({ preventScroll: true });
    sel.removeAllRanges();
    sel.addRange(saved);
  }

  function formatSelection(cmd: TextMarkCommand): boolean {
    const el = editorRef.current;
    if (!el || isFrame) return false;
    restoreEditorSelection();

    if (cmd.command === "list" || cmd.command === "indent") {
      const model = readEditorModel(el);
      if (!model) return false;
      const text = runsPlainText(model.runs);
      const edit =
        cmd.command === "list"
          ? toggleListLines(text, model.start, model.end, cmd.value)
          : indentListLine(text, model.start, cmd.value);
      // Команда наша, даже если строка не список (вкладывать нечего).
      if (edit) applyEditorEdit(model, edit);
      return true;
    }

    if (cmd.command === "link") {
      // Ссылка — через модель, как маркер. Курсор стоит в ссылке без
      // выделения — правим или снимаем её целиком.
      const model = readEditorModel(el);
      if (!model) return false;
      let { start, end } = model;
      if (start === end) {
        const hit = linkRangeAt(model.runs, start);
        if (!hit) return false;
        start = hit.start;
        end = hit.end;
      }
      writeEditorModel(el, runsSetHref(model.runs, start, end, cmd.value), model.start, model.end);
      scheduleLiveSync();
      return true;
    }

    const sel = window.getSelection();
    if (cmd.command === "fontSize" && (!sel || sel.isCollapsed)) {
      // Размер всей надписи: снимаем свои размеры у кусков прямо в поле,
      // иначе при сохранении они вернулись бы из поля обратно. Объект
      // поменяет панель — поэтому false.
      const model = readEditorModel(el);
      if (model && model.runs.some((r) => r.fontSize)) {
        writeEditorModel(el, runsWithoutFontSize(model.runs), model.start);
        scheduleLiveSync();
      }
      return false;
    }
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return false;
    const range = sel.getRangeAt(0);
    if (!el.contains(range.commonAncestorContainer)) return false;

    // Браузер должен ставить стили, а не теги <font> — их сложнее разбирать.
    try { document.execCommand("styleWithCSS", false, "true"); } catch { /* Safari */ }

    /** Переключить признак у выделенного куска (жирный, курсив и т.п.). */
    const toggle = (browserCommand: string) => {
      document.execCommand(browserCommand);
    };
    /** Обернуть выделение в span с нужным стилем (цвет, размер). */
    const wrap = (css: Record<string, string>) => {
      const span = document.createElement("span");
      for (const [k, v] of Object.entries(css)) span.style.setProperty(k, v);
      span.appendChild(range.extractContents());
      range.insertNode(span);
      const next = document.createRange();
      next.selectNodeContents(span);
      sel.removeAllRanges();
      sel.addRange(next);
    };

    switch (cmd.command) {
      case "bold":
        toggle("bold");
        break;
      case "italic":
        toggle("italic");
        break;
      case "underline":
        toggle("underline");
        break;
      case "strike":
        toggle("strikeThrough");
        break;
      case "color":
        wrap({ color: cmd.value });
        break;
      case "fontSize":
        {
          // В долях размера надписи: поле нарисовано в масштабе экрана, и
          // точки доски здесь давали не тот размер — на доске слово
          // выходило мельче, чем в поле.
          const base = Number(el.dataset.wbBaseFont) || textDisplayStyle(obj).fontSize;
          wrap({ "font-size": `${Math.round((cmd.value / base) * 10000) / 10000}em` });
        }
        break;
      case "highlight": {
        // Маркер — через модель: так его можно и снять, и он не путается
        // с цветом текста.
        const model = readEditorModel(el);
        if (!model) return false;
        const runs = runsSetHighlight(model.runs, model.start, model.end, cmd.value);
        writeEditorModel(el, runs, model.start, model.end);
        break;
      }
      default:
        return false;
    }
    scheduleLiveSync();
    return true;
  }

  /** Правка строк поля (список, отступ) с сохранением оформления. */
  function applyEditorEdit(model: EditorModel, edit: TextEdit) {
    const el = editorRef.current;
    if (!el) return;
    const runs = runsReplace(model.runs, edit.start, edit.end, edit.text);
    writeEditorModel(el, runs, edit.caret);
    scheduleLiveSync();
  }

  /**
   * Клавиши списков — как в Miro: Enter продолжает список и выходит из
   * него на пустом пункте, Tab вкладывает, Backspace у маркера снимает его.
   * true — клавиша обработана здесь.
   */
  function handleListKey(ev: React.KeyboardEvent): boolean {
    const el = editorRef.current;
    if (!el || isFrame) return false;
    const isEnter = ev.key === "Enter" && !ev.shiftKey;
    const isTab = ev.key === "Tab";
    const isBackspace = ev.key === "Backspace";
    if (!isEnter && !isTab && !isBackspace) return false;
    // Tab в поле набора не должен уводить фокус на кнопки страницы.
    if (isTab) ev.preventDefault();
    const model = readEditorModel(el);
    if (!model) return isTab;
    const text = runsPlainText(model.runs);
    const collapsed = model.start === model.end;
    let edit: TextEdit | null = null;
    if (isEnter && collapsed) edit = enterInList(text, model.start);
    else if (isTab) edit = indentListLine(text, model.start, ev.shiftKey ? -1 : 1);
    else if (isBackspace && collapsed) edit = backspaceAtMarker(text, model.start);
    if (!edit) return isTab;
    ev.preventDefault();
    applyEditorEdit(model, edit);
    return true;
  }

  /**
   * Вставка в надпись — только текст, как в Miro. Со страницы или из Word
   * в буфере лежит разметка: свой размер, цвет, белая заливка у каждого
   * куска. Её размер перебивал размер надписи — кнопка размера меняла одну
   * рамку, а текст оставался прежним.
   */
  function onEditorPaste(ev: React.ClipboardEvent<HTMLDivElement>) {
    const el = editorRef.current;
    if (!el || isFrame) return;
    ev.preventDefault();
    const raw = ev.clipboardData.getData("text/plain");
    // Картинку в надпись не вставить — молча ничего не делаем.
    if (!raw) return;
    const text = raw.replace(/\r\n?/g, "\n");
    const model = readEditorModel(el);
    if (!model) return;
    const runs = runsReplace(model.runs, model.start, model.end, text);
    writeEditorModel(el, runs, model.start + text.length);
    scheduleLiveSync();
  }

  /**
   * Курсор набора у края экрана — доска едет за ним, как в Miro. Раньше
   * длинная надпись уходила за край, и печатать приходилось вслепую.
   */
  function followCaret() {
    if (!onViewportChange || isFrame) return;
    const el = editorRef.current;
    const vpEl = viewportRef.current;
    const sel = window.getSelection();
    if (!el || !vpEl || !sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0).cloneRange();
    if (!el.contains(range.endContainer)) return;
    range.collapse(false);
    let r: DOMRect | undefined = range.getClientRects()[0];
    if (!r || (r.width === 0 && r.height === 0 && r.left === 0 && r.top === 0)) {
      // Курсор в пустой строке: у пустого места нет прямоугольника —
      // меряем по временной метке.
      const saved = sel.getRangeAt(0).cloneRange();
      const mark = document.createElement("span");
      mark.textContent = "​";
      range.insertNode(mark);
      r = mark.getBoundingClientRect();
      mark.remove();
      // Склеиваем разрезанный меткой текст и возвращаем курсор на место.
      el.normalize();
      sel.removeAllRanges();
      sel.addRange(saved);
    }
    if (!r) return;
    const box = vpEl.getBoundingClientRect();
    const vvp = window.visualViewport;
    const top = Math.max(box.top, vvp ? vvp.offsetTop : box.top);
    const bottom = Math.min(box.bottom, vvp ? vvp.offsetTop + vvp.height : box.bottom);
    const margin = 48;
    let dx = 0;
    let dy = 0;
    if (r.right > box.right - margin) dx = box.right - margin - r.right;
    else if (r.left < box.left + margin) dx = box.left + margin - r.left;
    if (r.bottom > bottom - margin) dy = bottom - margin - r.bottom;
    else if (r.top < top + margin) dy = top + margin - r.top;
    if (!dx && !dy) return;
    const cam = getLiveViewport();
    onViewportChange({ ...cam, panX: cam.panX + dx, panY: cam.panY + dy });
  }

  /** «- » в начале строки становится маркером списка. */
  function onEditorInput(ev: React.FormEvent<HTMLDivElement>) {
    const native = ev.nativeEvent as InputEvent;
    const el = editorRef.current;
    const typed =
      native.inputType === "insertText" || native.inputType === "insertCompositionText";
    if (el && !isFrame && typed && /\s/.test(native.data ?? "")) {
      const model = readEditorModel(el);
      if (model && model.start === model.end) {
        const edit = autoformatLine(runsPlainText(model.runs), model.start);
        if (edit) {
          applyEditorEdit(model, edit);
          requestAnimationFrame(followCaret);
          return;
        }
      }
    }
    scheduleLiveSync();
    // После отрисовки: поле уже выросло под новый текст.
    requestAnimationFrame(followCaret);
  }

  useEffect(() => {
    if (isFrame) return;
    const onSelectionChange = () => {
      const el = editorRef.current;
      const sel = window.getSelection();
      if (!el || !sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      if (el.contains(range.commonAncestorContainer)) savedRangeRef.current = range.cloneRange();
    };
    document.addEventListener("selectionchange", onSelectionChange);
    return () => document.removeEventListener("selectionchange", onSelectionChange);
  }, [isFrame]);

  useEffect(() => {
    if (isFrame) return;
    return registerTextSelectionLinkInfo(() => {
      const el = editorRef.current;
      if (!el) return null;
      restoreEditorSelection();
      const model = readEditorModel(el);
      if (!model) return { canLink: false, href: null };
      const hit = linkRangeAt(model.runs, model.start);
      if (model.start === model.end) return { canLink: Boolean(hit), href: hit?.href ?? null };
      // Выделение целиком внутри одной ссылки — покажем её адрес.
      const inside = hit && hit.start <= model.start && hit.end >= model.end && hit.end > model.start;
      return { canLink: true, href: inside ? hit.href : null };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFrame, objectId]);

  useEffect(() => {
    if (isFrame) return;
    return registerTextSelectionFormatter((cmd) => formatSelection(cmd));
    // formatSelection читает ref-ы, пересоздавать подписку не нужно
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFrame, objectId]);
/**
   * Размер набранного текста. Одно измерение и для рамки во время набора,
   * и для сохранения: раньше рамка считалась по DOM, а сохранялся результат
   * измерения — они расходились на пиксель-другой, и рамка дёргалась.
   */
  function measureDraft(
    current: ReturnType<typeof scene.findObject>,
    raw: string,
    html?: string
  ): { w: number; h: number } | null {
    if (!current || !isTextObject(current)) return null;
    const fixedWidth = textWidthMode(current) === "fixed";
    if (fixedWidth) {
      const baseW = Math.max(TEXT_BOX_MIN_W, Math.round(Number(current.w) || TEXT_BOX_MIN_W));
      const curW = grownTextWidth(current, raw, textDisplayStyle(current), baseW, html);
      const fitted = measureTextObjectSizeAtWidth(
        raw || " ",
        textDisplayStyle(current),
        curW,
        html
      );
      return { w: curW, h: Math.max(TEXT_BOX_MIN_H, fitted.h) };
    }
    const fitted = measureTextObjectSize(
      raw || " ",
      textDisplayStyle(current),
      TEXT_BOX_MAX_W,
      html
    );
    return {
      w: Math.min(TEXT_BOX_MAX_W, Math.max(TEXT_BOX_MIN_W, fitted.w)),
      h: Math.max(TEXT_BOX_MIN_H, fitted.h),
    };
  }

  /** Размер набираемой надписи — чтобы рамка с ручками шла вровень. */
  function pushBoxHint() {
    const current = scene.findObject(objectId);
    if (!current || isFrame || !isTextObject(current)) return;
    const runs = runsPatch(readDraftRuns()).runs ?? null;
    const fitted = measureDraft(
      current,
      readDraftRaw(),
      runs ? runsToHtml(runs) : undefined
    );
    if (fitted) scene.setEditingBoxHint?.(objectId, fitted);
  }

  function scheduleLiveSync() {
    pushBoxHint();
    if (liveTimerRef.current) window.clearTimeout(liveTimerRef.current);
    liveTimerRef.current = window.setTimeout(() => {
      liveTimerRef.current = 0;
      flushLiveSync();
    }, 120);
  }

  useEffect(() => {
    if (!obj) return;
    closingRef.current = false;
    scene.setTextEditing?.(objectId);
    scene.refreshSelectionChrome?.();
    const el = isFrame ? inputRef.current : editorRef.current;
    if (!el) return;

    const current = scene.findObject(objectId);
    const initial = current ? textContent(current) : "";
    editStartRef.current = {
      text: initial,
      w: Math.round(Number(current?.w) || 0),
      h: Math.round(Number(current?.h) || 0),
      runs: current ? objectRuns(current) : null,
    };
    lastLiveSentRef.current = "";

    if (isFrame && el instanceof HTMLInputElement) {
      el.value = initial;
      el.focus();
      el.select();
    } else {
      const initialRuns = current ? objectRuns(current) : null;
      // Размер надписи — на само поле: по нему размеры кусков пишутся в
      // долях, и поле в масштабе экрана показывает их верно.
      if (current && isTextLikeObject(current)) {
        el.dataset.wbBaseFont = String(textDisplayStyle(current).fontSize);
      }
      if (initialRuns) el.innerHTML = runsToHtml(initialRuns, Number(el.dataset.wbBaseFont) || undefined);
      else el.textContent = initial;
      el.focus();
      // Открыли двойным щелчком — курсор в место щелчка; клавишей — в конец.
      const click =
        lastPointerDown && Date.now() - lastPointerDown.at < CLICK_TO_EDIT_MS
          ? lastPointerDown
          : null;
      let range = click ? caretRangeAt(click.x, click.y) : null;
      if (!range || !el.contains(range.startContainer)) {
        range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
      }
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
    // После появления клавиатуры visualViewport сжимается — держим поле в зоне видимости.
    requestAnimationFrame(() => {
      el.scrollIntoView({ block: "nearest", inline: "nearest" });
    });

    const DRAG_PX = 6;
    let pendingOutside: { pointerId: number; x: number; y: number } | null = null;
    let blurTimer: number | null = null;
    let outsideMove: ((ev: PointerEvent) => void) | null = null;
    let outsideUp: ((ev: PointerEvent) => void) | null = null;

    function focusEditor() {
      const focusEl = isFrame ? inputRef.current : editorRef.current;
      focusEl?.focus({ preventScroll: true });
    }

    function clearBlurTimer() {
      if (blurTimer != null) {
        window.clearTimeout(blurTimer);
        blurTimer = null;
      }
    }

    function detachOutsideWatch() {
      if (outsideMove) {
        document.removeEventListener("pointermove", outsideMove, true);
        outsideMove = null;
      }
      if (outsideUp) {
        document.removeEventListener("pointerup", outsideUp, true);
        document.removeEventListener("pointercancel", outsideUp, true);
        outsideUp = null;
      }
      pendingOutside = null;
    }

    function confirmCloseOutside() {
      if (closingRef.current) return;
      clearBlurTimer();
      detachOutsideWatch();
      closingRef.current = true;
      // Снимаем блок инструментов — иначе следующий клик игнорируется
      // (isInteractionBlocked), и обводка/выделение уходят только со второго нажатия.
      scene.setTextEditing?.(null);
      if (!isFrame) {
        scene.setSelection([]);
      }
      void commitAndClose();
    }

    /** Pan/жест камеры — оставляем окно ввода и возвращаем фокус. */
    function keepEditingAfterCameraGesture() {
      clearBlurTimer();
      detachOutsideWatch();
      requestAnimationFrame(focusEditor);
    }

    function onDocKeyDown(ev: KeyboardEvent) {
      if (ev.key !== "Tab") return;
      const focusEl = isFrame ? inputRef.current : editorRef.current;
      if (!focusEl) return;
      ev.preventDefault();
      focusEl.focus();
    }

    function onPointerDownOutside(ev: PointerEvent) {
      const root = isFrame ? inputRef.current : editorRef.current;
      if (!root || closingRef.current) return;
      const target = ev.target as Node | null;
      if (target && root.contains(target)) return;
      if (target instanceof Element && target.closest(".wb-selection-toolbar")) return;
      // Ручки размера — часть работы с этой же надписью, набор не закрываем.
      if (target instanceof Element && target.closest(".wb-selection-frame")) return;

      // Middle/right — панорама камеры, не закрываем ввод.
      if (ev.button === 1 || ev.button === 2) {
        keepEditingAfterCameraGesture();
        return;
      }
      if (ev.button !== 0) return;

      // Клик vs drag: drag/pan не должен уничтожать пустой текст.
      pendingOutside = { pointerId: ev.pointerId, x: ev.clientX, y: ev.clientY };

      outsideMove = (moveEv: PointerEvent) => {
        const p = pendingOutside;
        if (!p || moveEv.pointerId !== p.pointerId) return;
        if (Math.hypot(moveEv.clientX - p.x, moveEv.clientY - p.y) > DRAG_PX) {
          keepEditingAfterCameraGesture();
        }
      };
      outsideUp = (upEv: PointerEvent) => {
        const p = pendingOutside;
        if (!p || upEv.pointerId !== p.pointerId) return;
        // Короткий клик без заметного сдвига — закрыть и закоммитить.
        confirmCloseOutside();
      };
      document.addEventListener("pointermove", outsideMove, true);
      document.addEventListener("pointerup", outsideUp, true);
      document.addEventListener("pointercancel", outsideUp, true);
    }

    function onViewportGestureStart() {
      keepEditingAfterCameraGesture();
    }

    function onEditorBlur() {
      if (closingRef.current) return;
      clearBlurTimer();
      // Ждём pointerup/pan: blur приходит раньше, чем понятно — клик это или движение доски.
      blurTimer = window.setTimeout(() => {
        blurTimer = null;
        if (closingRef.current) return;
        if (pendingOutside) return;
        const focusEl = isFrame ? inputRef.current : editorRef.current;
        if (focusEl && document.activeElement === focusEl) return;
        // Фокус ушёл в поле панели надписи (размер шрифта) — это всё ещё
        // работа с этой надписью: набор не закрываем, иначе пропадало
        // выделение и размер ложился на всю надпись.
        const active = document.activeElement;
        if (active instanceof Element && active.closest(".wb-selection-toolbar")) return;
        confirmCloseOutside();
      }, 0);
    }

    const blurTarget = isFrame ? inputRef.current : editorRef.current;
    blurTarget?.addEventListener("blur", onEditorBlur);

    document.addEventListener("keydown", onDocKeyDown, true);
    document.addEventListener("pointerdown", onPointerDownOutside, true);
    window.addEventListener("wb:viewport-gesture-start", onViewportGestureStart);
    return () => {
      if (liveTimerRef.current) {
        window.clearTimeout(liveTimerRef.current);
        liveTimerRef.current = 0;
      }
      clearBlurTimer();
      detachOutsideWatch();
      blurTarget?.removeEventListener("blur", onEditorBlur);
      scene.setTextEditing?.(null);
      scene.refreshSelectionChrome?.();
      document.removeEventListener("keydown", onDocKeyDown, true);
      document.removeEventListener("pointerdown", onPointerDownOutside, true);
      window.removeEventListener("wb:viewport-gesture-start", onViewportGestureStart);
    };
    // objectId only: смена стиля с панели не должна сбрасывать набор
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectId, scene, isFrame]);

  async function commitAndClose() {
    if (liveTimerRef.current) {
      window.clearTimeout(liveTimerRef.current);
      liveTimerRef.current = 0;
    }

    const start = editStartRef.current;

    if (isFrame) {
      const el = inputRef.current;
      if (!el) {
        onClose();
        return;
      }
      const nextTitle = el.value.replace(/\r\n/g, "\n").trim() || "Рамка";
      const current = scene.findObject(objectId);
      if (!current || !isFrameObject(current)) {
        onClose();
        return;
      }
      const startTitle = start?.text ?? textContent(current);
      if (nextTitle === startTitle) {
        // Live мог оставить незакоммиченный trim — догоняем без лишнего undo.
        if (nextTitle !== textContent(current)) {
          await scene.commitBatchPatches(
            [{ id: objectId, patch: { title: nextTitle } }],
            `frame-title-${objectId}-${Date.now()}`,
            null
          );
        }
        onClose();
        return;
      }
      const patch = { title: nextTitle };
      const undoEntry = {
        forward: [{ type: "update_object" as const, id: objectId, patch }],
        inverse: [
          {
            type: "update_object" as const,
            id: objectId,
            patch: { title: startTitle || "Рамка" },
          },
        ],
      };
      await scene.commitBatchPatches(
        [{ id: objectId, patch }],
        `frame-title-${objectId}-${Date.now()}`,
        undoEntry
      );
      onClose();
      return;
    }

    const el = editorRef.current;
    if (!el) {
      onClose();
      return;
    }
    // Текст — из тех же кусков, что сохраняются, и обрезаны они одинаково.
    // Раньше текст брался из innerText и обрезался, а куски — нет: вставка
    // со страницы приносила пустые строки сверху, доска рисовала их, а рамку
    // мерила по тексту без них — и текст выпадал из рамки.
    // Адреса в тексте становятся ссылками, как в Miro.
    const draftRuns = runsAutolink(runsTrimEdges(readDraftRuns()));
    const nextText = runsPlainText(draftRuns);
    const current = scene.findObject(objectId);
    if (!current) {
      onClose();
      return;
    }

    if (!nextText) {
      if (isTextObject(current)) {
        const fixedWidth = textWidthMode(current) === "fixed";
        if (!fixedWidth) {
          await scene.deleteObject(objectId);
        }
      } else {
        const startText = start?.text ?? "";
        const undoEntry = {
          forward: [
            { type: "update_object" as const, id: objectId, patch: { text: "", runs: null } },
          ],
          inverse: [
            {
              type: "update_object" as const,
              id: objectId,
              patch: {
                text: startText,
                runs: start?.runs ?? null,
                ...(start ? { w: start.w, h: start.h } : {}),
              },
            },
          ],
        };
        await scene.commitBatchPatches(
          [{ id: objectId, patch: { text: "", runs: null } }],
          `text-clear-${objectId}`,
          undoEntry
        );
      }
      onClose();
      return;
    }

    // Оформление кусков и размер считаем тем же измерением, что и во время
    // набора: раньше финальный размер мерили без разметки кусков, и после
    // закрытия поля цветной/жирный кусок обрезался.
    const nextRuns = runsPatch(draftRuns).runs ?? null;
    const fitted = measureDraft(
      current,
      nextText,
      nextRuns ? runsToHtml(nextRuns) : undefined
    );

    const startText = start?.text ?? "";
    const startW = start?.w ?? Math.round(Number(current.w) || 0);
    const startH = start?.h ?? Math.round(Number(current.h) || 0);
    const startRuns = start?.runs ?? null;
    const sizeChangedFromStart = Boolean(
      fitted && (fitted.w !== startW || fitted.h !== startH)
    );
    const runsChangedFromStart =
      JSON.stringify(startRuns ?? null) !== JSON.stringify(nextRuns);
    if (nextText === startText.trim() && !sizeChangedFromStart && !runsChangedFromStart) {
      onClose();
      return;
    }

    const patch: Record<string, unknown> = { text: nextText, runs: nextRuns };
    if (fitted) {
      patch.w = fitted.w;
      patch.h = fitted.h;
    }
    const inversePatch: Record<string, unknown> = {
      text: startText,
      runs: startRuns ?? null,
    };
    if (isTextObject(current) && start) {
      inversePatch.w = startW;
      inversePatch.h = startH;
    }
    const undoEntry = {
      forward: [{ type: "update_object" as const, id: objectId, patch }],
      inverse: [{ type: "update_object" as const, id: objectId, patch: inversePatch }],
    };
    await scene.commitBatchPatches(
      [{ id: objectId, patch }],
      `text-edit-${objectId}-${Date.now()}`,
      undoEntry
    );
    onClose();
  }

  if (!obj || !isOverlayEditableObject(obj)) return null;

  const display = textDisplayStyle(obj);
  const vpEl = viewportRef.current;
  if (!vpEl) return null;

  const w = typeof obj.w === "number" ? obj.w : 160;
  const h = typeof obj.h === "number" ? obj.h : 40;
  const autoGrow = isTextObject(obj);
  // Ширина, которую может растить набор (после первого Enter), — поле растёт
  // сразу, как у надписи без фиксированной ширины, но не уже рамки. Иначе до
  // сохранения текст на миг переносился в узком поле.
  const wrapToBox =
    isTextObject(obj) && textWidthMode(obj) === "fixed" && !textWidthGrows(obj);
  const rect = vpEl.getBoundingClientRect();
  const scale = cam.scale > 0 ? cam.scale : 1;
  // Компактное поле у угла — не растягивать на ширину рамки (иначе перекрывает тулбар).
  const frameTitleScreenW = isFrame
    ? Math.min(
        280,
        Math.max(128, (textContent(obj).length + 3) * display.fontSize * scale * 0.62)
      )
    : 0;
  const titleOffsetY = isFrame ? -30 : 0;
  const tl = worldToClient(obj.x, obj.y + titleOffsetY, rect, cam);
  const br = worldToClient(
    obj.x + w,
    obj.y + (isFrame ? titleOffsetY + 24 : h),
    rect,
    cam
  );
  const boxW = isFrame ? frameTitleScreenW : Math.max(40, br.x - tl.x);
  const boxH = Math.max(24, isFrame ? 28 : br.y - tl.y);
  // Раньше поле набора всегда затягивалось в видимую область, поэтому при
  // движении по доске оно «ехало» за экраном. Теперь оно стоит на своём месте
  // на доске; притягиваем только когда экран поджала экранная клавиатура,
  // иначе поле окажется под ней и печатать вслепую.
  const layoutH = typeof window !== "undefined" ? window.innerHeight : vv.height;
  const keyboardOpen = vv.height > 0 && layoutH - vv.height > 120;
  const pinned = keyboardOpen
    ? clampRectToVisualViewport(tl.x, tl.y, boxW, boxH, vv, 8)
    : { left: tl.x, top: tl.y };

  function stopShortcutKeys(ev: React.KeyboardEvent) {
    ev.stopPropagation();
  }

  function onKeyDown(ev: React.KeyboardEvent) {
    stopShortcutKeys(ev);
    const mod = ev.ctrlKey || ev.metaKey;

    if (mod && (ev.code === "KeyB" || ev.code === "KeyI" || ev.code === "KeyU") && !isFrame) {
      ev.preventDefault();
      const st = textDisplayStyle(obj);
      let style: Record<string, unknown> = {};
      if (ev.code === "KeyB") {
        const next = st.fontWeight === "bold" ? "normal" : "bold";
        style = { fontWeight: next };
        if (isTextObject(obj)) setTextSettings({ fontWeight: next });
      } else if (ev.code === "KeyI") {
        const next = st.fontStyle === "italic" ? "normal" : "italic";
        style = { fontStyle: next };
        if (isTextObject(obj)) setTextSettings({ fontStyle: next });
      } else {
        const deco = st.textDecoration || "";
        const on = /\bunderline\b/.test(deco);
        const strike = /\bline-through\b/.test(deco);
        const parts: string[] = [];
        if (!on) parts.push("underline");
        if (strike) parts.push("line-through");
        const next = parts.length ? parts.join(" ") : "none";
        style = { textDecoration: next };
        if (isTextObject(obj)) setTextSettings({ textDecoration: next });
      }
      // Выделен кусок — оформляем только его; иначе всю надпись.
      const hotkeyCmd: TextMarkCommand =
        ev.code === "KeyB"
          ? { command: "bold" }
          : ev.code === "KeyI"
            ? { command: "italic" }
            : { command: "underline" };
      if (formatSelection(hotkeyCmd)) return;
      void scene.commitObjectPatch(
        objectId,
        patchTextStyleAndFit(obj, style),
        `text-style-${objectId}`
      );
      return;
    }

    // Ctrl+K — ссылка на выделенный кусок, окошко на панели.
    if (mod && ev.code === "KeyK" && !isFrame) {
      ev.preventDefault();
      window.dispatchEvent(new CustomEvent(TEXT_LINK_REQUEST_EVENT));
      return;
    }

    if (ev.key === "Escape") {
      ev.preventDefault();
      closingRef.current = true;
      void commitAndClose();
      return;
    }
    if (!mod && !ev.altKey) handleListKey(ev);
    // Первый перенос строки фиксирует ширину: дальше строки переносятся здесь же,
    // а ширину при желании меняют ручками. Раньше надпись продолжала тянуться
    // вправо, и перенос «уезжал».
    if (
      ev.key === "Enter" &&
      !mod &&
      !isFrame &&
      isTextObject(obj) &&
      textWidthMode(obj) === "auto"
    ) {
      // Ширину берём по тому, что набрано сейчас, а не из сохранённого объекта:
      // сохранение идёт с задержкой, и при быстром наборе ширина фиксировалась
      // старая — текст ложился узким столбиком.
      const current = scene.findObject(objectId) ?? obj;
      const draftRuns = readDraftRuns();
      const fitted = measureDraft(
        current,
        readDraftRaw(),
        draftRuns.length ? runsToHtml(draftRuns) : undefined
      );
      const width = Math.max(
        TEXT_BOX_MIN_W,
        Math.round(Math.max(Number(current.w) || 0, fitted?.w ?? 0))
      );
      void scene.commitObjectPatch(
        objectId,
        // Ширина фиксируется, но может расти до самой длинной строки.
        { w: width, meta: mergeTextMeta(obj, { textWidthMode: "fixed", textWidthGrow: true }) },
        `text-wrap-${objectId}`
      );
    }

    const commitCombo = ev.key === "Enter" && (mod || (!ev.shiftKey && isFrame));
    if (commitCombo) {
      ev.preventDefault();
      closingRef.current = true;
      void commitAndClose();
    }
  }

  const sharedBoxStyle: React.CSSProperties = {
    position: "fixed",
    left: pinned.left,
    top: pinned.top,
    right: "auto",
    bottom: "auto",
    maxHeight: Math.max(48, vv.height - 16),
    // У надписи рамка одна — та, что с ручками (selectionFrame). Своя обводка
    // поля набора давала вторую рамку, и при быстром наборе они расходились.
    outline: isTextObject(obj) ? "none" : "2px solid rgba(98, 70, 234, 0.7)",
    border: "none",
    boxSizing: "border-box",
  };

  if (isFrame) {
    return (
      <input
        ref={inputRef}
        type="text"
        className="wb-text-overlay-editor wb-text-overlay-editor--frame-title"
        aria-label="Название рамки"
        maxLength={256}
        defaultValue={textContent(obj)}
        style={{
          ...sharedBoxStyle,
          width: frameTitleScreenW,
          height: 28,
          color: display.color,
          fontSize: display.fontSize * scale,
          fontFamily: display.fontFamily,
          fontWeight: 600,
          lineHeight: 1.2,
          background: "rgba(255,255,255,0.96)",
          padding: "4px 8px",
          zIndex: 220,
          borderRadius: 4,
          boxShadow: "0 4px 16px rgba(15, 23, 42, 0.12)",
        }}
        onMouseDown={(ev) => ev.stopPropagation()}
        onPointerDown={(ev) => ev.stopPropagation()}
        onKeyDown={onKeyDown}
        onKeyUp={stopShortcutKeys}
        onChange={() => scheduleLiveSync()}
        onInput={() => scheduleLiveSync()}
      />
    );
  }

  return (
    <div
      ref={editorRef}
      className={`wb-text-overlay-editor${
        isStickyObject(obj) ? " wb-text-overlay-editor--sticky" : ""
      }${autoGrow ? " wb-text-overlay-editor--auto" : ""}`}
      contentEditable
      suppressContentEditableWarning
      // Размер надписи: размеры кусков в поле пишутся в долях от него.
      data-wb-base-font={display.fontSize}
      role="textbox"
      aria-label={isStickyObject(obj) ? "Текст стикера" : "Текст"}
      style={{
        ...sharedBoxStyle,
        width: wrapToBox
          ? Math.max(40, br.x - tl.x)
          : autoGrow
            ? "max-content"
            : Math.max(40, br.x - tl.x),
        maxWidth: wrapToBox
          ? Math.max(40, br.x - tl.x)
          : autoGrow
            ? TEXT_BOX_MAX_W * scale
            : undefined,
        minWidth: wrapToBox
          ? Math.max(40, br.x - tl.x)
          : autoGrow
            ? Math.max(TEXT_BOX_MIN_W, w) * scale
            : undefined,
        minHeight: autoGrow
          ? Math.max(TEXT_BOX_MIN_H, display.fontSize * display.lineHeight + 8) *
            scale
          : Math.max(24, br.y - tl.y),
        height: autoGrow ? "auto" : undefined,
        color: display.color,
        fontSize: display.fontSize * scale,
        fontFamily: display.fontFamily,
        fontWeight: display.fontWeight || "normal",
        fontStyle: display.fontStyle || "normal",
        textDecoration: display.textDecoration || "none",
        textAlign: display.textAlign as React.CSSProperties["textAlign"],
        lineHeight: display.lineHeight,
        background: display.background ?? "transparent",
        padding: isStickyObject(obj)
          ? `${10 * scale}px ${12 * scale}px`
          : `${4 * scale}px ${6 * scale}px`,
        zIndex: 200,
        borderRadius: isStickyObject(obj) ? 4 : display.background ? 6 * scale : 2,
        boxShadow: isStickyObject(obj) ? "0 2px 8px rgba(0,0,0,0.08)" : undefined,
        overflow: autoGrow && !wrapToBox ? "visible" : wrapToBox ? "hidden" : "auto",
        whiteSpace: "pre-wrap",
        wordBreak: wrapToBox || !autoGrow ? "break-word" : "normal",
        overflowWrap: "break-word",
      }}
      onMouseDown={(ev) => ev.stopPropagation()}
      onPointerDown={(ev) => ev.stopPropagation()}
      onKeyDown={onKeyDown}
      onKeyUp={(ev) => {
        stopShortcutKeys(ev);
        if (/^(Arrow|Home|End|Page)/.test(ev.key)) followCaret();
      }}
      onInput={onEditorInput}
      onPaste={onEditorPaste}
      onClick={(ev) => {
        // Ctrl+щелчок по ссылке во время набора — открыть её.
        if (!(ev.ctrlKey || ev.metaKey)) return;
        const a = (ev.target as Element | null)?.closest?.("a[data-wb-link]");
        const href = a?.getAttribute("href");
        if (!href) return;
        ev.preventDefault();
        window.open(href, "_blank", "noopener,noreferrer");
      }}
    />
  );
}
